import React, { useEffect, useRef, useState } from "react";

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function fract(value) {
  return value - Math.floor(value);
}

function hashNoise(x, y, seed) {
  return fract(Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453123);
}

function easeOutCubic(value) {
  return 1 - (1 - value) ** 3;
}

function copyComputedStyles(sourceNode, targetNode) {
  const computed = window.getComputedStyle(sourceNode);
  const targetStyle = targetNode.style;

  Array.from(computed).forEach((propertyName) => {
    targetStyle.setProperty(
      propertyName,
      computed.getPropertyValue(propertyName),
      computed.getPropertyPriority(propertyName)
    );
  });

  targetStyle.setProperty("animation", "none");
  targetStyle.setProperty("transition", "none");
  targetStyle.setProperty("caret-color", "transparent");
}

function cloneNodeWithInlineStyles(node, width, height) {
  const clone = node.cloneNode(true);
  const sourceNodes = [node, ...node.querySelectorAll("*")];
  const targetNodes = [clone, ...clone.querySelectorAll("*")];

  sourceNodes.forEach((sourceNode, index) => {
    const targetNode = targetNodes[index];
    if (!targetNode) return;
    copyComputedStyles(sourceNode, targetNode);
  });

  clone.style.setProperty("width", `${width}px`);
  clone.style.setProperty("height", `${height}px`);
  clone.style.setProperty("margin", "0");
  clone.style.setProperty("transform", "none");
  clone.style.setProperty("overflow", "hidden");

  return clone;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });
}

function parseCssTimeToMs(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return 0;
  if (trimmed.endsWith("ms")) return Number.parseFloat(trimmed) || 0;
  if (trimmed.endsWith("s")) return (Number.parseFloat(trimmed) || 0) * 1000;
  return Number.parseFloat(trimmed) || 0;
}

function getMaxCssAnimationTime(style) {
  const durations = String(style.animationDuration || "0s").split(",");
  const delays = String(style.animationDelay || "0s").split(",");
  const count = Math.max(durations.length, delays.length);
  let maxTime = 0;

  for (let index = 0; index < count; index += 1) {
    const duration = parseCssTimeToMs(durations[index] || durations[durations.length - 1]);
    const delay = parseCssTimeToMs(delays[index] || delays[delays.length - 1]);
    maxTime = Math.max(maxTime, duration + delay);
  }

  return maxTime;
}

function waitForTimeout(duration, signal) {
  if (!duration || duration <= 0) return Promise.resolve();

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(resolve, duration);

    if (!signal) return;
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timeoutId);
        resolve();
      },
      { once: true }
    );
  });
}

async function waitForStableDissolveLayout(node, signal) {
  const enteringPanel = node?.closest?.(".mobile-day-panel-enter");
  if (!(enteringPanel instanceof HTMLElement)) return;

  const panelStyle = window.getComputedStyle(enteringPanel);
  if (!panelStyle.animationName || panelStyle.animationName === "none") return;

  const settleDelay = getMaxCssAnimationTime(panelStyle);
  await waitForTimeout(settleDelay > 0 ? settleDelay + 34 : 340, signal);
}

async function rasterizeSvgMarkup(svgMarkup, width, height, scale = 1) {
  const pixelWidth = Math.max(1, Math.round(width * scale));
  const pixelHeight = Math.max(1, Math.round(height * scale));

  const drawToCanvas = (drawable) => {
    const canvas = document.createElement("canvas");
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(drawable, 0, 0, pixelWidth, pixelHeight);
    return canvas;
  };

  const blob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });

  if (typeof window.createImageBitmap === "function") {
    try {
      const bitmap = await window.createImageBitmap(blob);
      const canvas = drawToCanvas(bitmap);
      if (typeof bitmap.close === "function") bitmap.close();
      return canvas;
    } catch {
      // fall through to Image-based decoding
    }
  }

  try {
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
    const image = await loadImage(dataUrl);
    return drawToCanvas(image);
  } catch {
    const objectUrl = URL.createObjectURL(blob);
    try {
      const image = await loadImage(objectUrl);
      return drawToCanvas(image);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

async function rasterizeElement(node) {
  const rect = node.getBoundingClientRect();
  const cssWidth = Math.max(1, rect.width);
  const cssHeight = Math.max(1, rect.height);
  const scale = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.max(1, Math.round(cssWidth * scale));
  const height = Math.max(1, Math.round(cssHeight * scale));
  const clone = cloneNodeWithInlineStyles(node, cssWidth, cssHeight);
  const wrapper = document.createElement("div");

  wrapper.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
  wrapper.style.setProperty("width", `${cssWidth}px`);
  wrapper.style.setProperty("height", `${cssHeight}px`);
  wrapper.style.setProperty("overflow", "hidden");
  wrapper.style.setProperty("display", "block");
  wrapper.appendChild(clone);

  const serialized = new XMLSerializer().serializeToString(wrapper);
  const svgMarkup = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${cssWidth}" height="${cssHeight}" viewBox="0 0 ${cssWidth} ${cssHeight}">
      <foreignObject width="100%" height="100%">${serialized}</foreignObject>
    </svg>
  `;

  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // ignore font loading issues and try to rasterize anyway
    }
  }

  const canvas = await rasterizeSvgMarkup(svgMarkup, cssWidth, cssHeight, scale);
  const context = canvas.getContext("2d", { willReadFrequently: true });
    const imageData = context.getImageData(0, 0, width, height);
    let opaquePixels = 0;

    for (let index = 3; index < imageData.data.length; index += 4) {
      if (imageData.data[index] > 20) {
        opaquePixels += 1;
      }
    }

    if (opaquePixels < Math.max(24, Math.floor(width * height * 0.01))) {
      throw new Error("telegram dissolve snapshot is blank");
    }

    return {
      rect,
      cssWidth,
      cssHeight,
      width,
      height,
      scale,
      imageData,
    };
}

function collectOpaquePixels(imageData, alphaThreshold = 16) {
  const { data, width, height } = imageData;
  const particles = [];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const alpha = data[index + 3];
      if (alpha < alphaThreshold) continue;

      particles.push({
        x,
        y,
        r: data[index],
        g: data[index + 1],
        b: data[index + 2],
        a: alpha,
        size: 1,
      });
    }
  }

  return particles;
}

// Groups pixels into clusterSize×clusterSize blocks — 9× fewer particles for Thanos mode.
function collectThanosClusters(imageData, clusterSize = 3, alphaThreshold = 16) {
  const { data, width, height } = imageData;
  const particles = [];
  const half = Math.floor(clusterSize / 2);

  for (let y = half; y < height - half; y += clusterSize) {
    for (let x = half; x < width - half; x += clusterSize) {
      const index = (y * width + x) * 4;
      if (data[index + 3] < alphaThreshold) continue;
      particles.push({
        x, y,
        r: data[index],
        g: data[index + 1],
        b: data[index + 2],
        a: data[index + 3],
        size: clusterSize,
      });
    }
  }
  return particles;
}

function buildHorizontalFront(width, progress, y, seed) {
  const introDistance = Math.max(30, width * 0.14);
  const turbulence = progress <= 0 ? 0 : 0.28 + progress * 0.72;
  const travel = -introDistance + progress * (width + 24 + introDistance);
  const wave = Math.sin(y * 0.052 + progress * 7.4 + seed) * 6 * turbulence;
  const jitter = (hashNoise(y * 0.19, progress * 9.1, seed) - 0.5) * 8 * turbulence;
  return travel + wave + jitter;
}

function normalizeVector(x, y) {
  const length = Math.hypot(x, y) || 1;
  return {
    x: x / length,
    y: y / length,
  };
}

function resolveUpwardScatter(seedX, seedY, progress) {
  const horizontal = (hashNoise(seedX * 0.21, seedY * 0.13, progress + 0.17) - 0.5) * 0.96;
  const vertical = -(1.3 + hashNoise(seedX * 0.09, seedY * 0.31, progress + 0.43) * 0.9);
  return normalizeVector(horizontal, vertical);
}

function resolveWindDrift(seedX, seedY, progress, seed) {
  const gustWave = Math.sin(seedY * 0.034 + progress * 6.8 + seed * 0.12);
  const crossWave = Math.cos(seedX * 0.028 - progress * 5.2 + seed * 0.07);
  const noiseX = hashNoise(seedX * 0.05, seedY * 0.12, seed + progress * 2.1) - 0.5;
  const noiseY = hashNoise(seedX * 0.11, seedY * 0.06, seed + progress * 3.4) - 0.5;

  return {
    x: gustWave * 0.9 + crossWave * 0.5 + noiseX * 1.8,
    y: -(0.34 + Math.abs(crossWave) * 0.42) + noiseY * 0.36,
  };
}

function resolveParticleTint(particle, scatterAmount, noise, lingerProgress) {
  const emphasis = clamp(scatterAmount * 1.28 + lingerProgress * 0.36, 0, 1);
  const luminance = (particle.r * 0.2126 + particle.g * 0.7152 + particle.b * 0.0722) / 255;
  const contrast = 1 + emphasis * 0.36;

  const applyContrast = (value) => clamp(((value / 255 - 0.5) * contrast + 0.5) * 255, 0, 255);
  let r = applyContrast(particle.r);
  let g = applyContrast(particle.g);
  let b = applyContrast(particle.b);

  const target =
    luminance > 0.72
      ? { r: 126 + noise * 18, g: 152 + noise * 16, b: 196 + noise * 20 }
      : { r: 232 + noise * 10, g: 241 + noise * 8, b: 255 };
  const mix = luminance > 0.72 ? 0.16 + emphasis * 0.42 : 0.08 + emphasis * 0.24;

  r = r + (target.r - r) * mix;
  g = g + (target.g - g) * mix;
  b = b + (target.b - b) * mix;

  return {
    r: Math.round(clamp(r, 0, 255)),
    g: Math.round(clamp(g, 0, 255)),
    b: Math.round(clamp(b, 0, 255)),
    alphaBoost: 1 + emphasis * 0.16,
  };
}

function resolveDissolveTarget(node) {
  if (!node) return null;
  const directChild = node.firstElementChild;

  if (directChild instanceof HTMLElement) {
    const rect = directChild.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return directChild;
    }
  }

  return node;
}

export async function playTelegramDissolve(node, options = {}) {
  if (!node || typeof window === "undefined") return;

  const {
    duration = 1280,
    introHoldDuration = 150,
    handoffDuration = 0,
    lingerDuration = 300,
    signal,
    mode = "sweep", // "sweep" = left-to-right front, "uniform" = all pixels die randomly
  } = options;

  const targetNode = resolveDissolveTarget(node);
  if (mode === "thanos") {
    // Wait for the card's grid expand transition (300ms) to finish before snapshot.
    await waitForTimeout(320, signal);
  } else {
    await waitForStableDissolveLayout(targetNode, signal);
  }
  if (signal?.aborted) return;
  const snapshot = await rasterizeElement(targetNode);
  if (!snapshot) return;
  if (signal?.aborted) return;

  const { rect, width, height, scale, imageData } = snapshot;
  const hostRect = node.getBoundingClientRect();
  const activePixels = mode === "thanos"
    ? collectThanosClusters(imageData, 3)
    : collectOpaquePixels(imageData);
  const burstPadding = Math.max(Math.ceil(18 * scale), Math.ceil(Math.max(width, height) * 0.22));
  const expandedWidth = width + burstPadding * 2;
  const expandedHeight = height + burstPadding * 2;
  const overlayCanvas = document.createElement("canvas");
  const overlayContext = overlayCanvas.getContext("2d");
  const frameCanvas = document.createElement("canvas");
  const frameContext = frameCanvas.getContext("2d");
  const frameImage = frameContext.createImageData(expandedWidth, expandedHeight);
  const framePixels = frameImage.data;
  const frontByRow = new Float32Array(height);
  const streamByRow = new Float32Array(height);
  const seed = Math.random() * 1000;

  // Uniform mode: pre-compute per-particle constants (deathTime, noise, unitX/Y, latJitter).
  // Eliminates 4-5 hashNoise calls per pixel per frame (~24M sin() ops/sec for typical card).
  const UNIFORM_FIELDS = 5; // deathTime, noise, unitX, unitY, latJitter
  const UNIFORM_EDGE_ZONE = 0.22;
  let uniformExtras = null;
  let uniformSortedIdx = null;
  let liveStart = 0;
  if (mode === "uniform") {
    uniformExtras = new Float32Array(activePixels.length * UNIFORM_FIELDS);
    for (let i = 0; i < activePixels.length; i++) {
      const p = activePixels[i];
      const dt = hashNoise(p.x * 0.07, p.y * 0.07, seed);
      const ns = hashNoise(p.x * 0.42, p.y * 0.37, seed);
      const lj = (hashNoise(p.x * 0.61, p.y * 0.29, seed) - 0.5) * 8;
      const uh = (hashNoise(p.x * 0.21, p.y * 0.13, seed + 0.17) - 0.5) * 0.96;
      const uv = -(1.3 + hashNoise(p.x * 0.09, p.y * 0.31, seed + 0.43) * 0.9);
      const uLen = Math.hypot(uh, uv) || 1;
      const off = i * UNIFORM_FIELDS;
      uniformExtras[off]     = dt;
      uniformExtras[off + 1] = ns;
      uniformExtras[off + 2] = lj;
      uniformExtras[off + 3] = uh / uLen;
      uniformExtras[off + 4] = uv / uLen;
    }
    // Sort indices by deathTime so fully-dead particles can be skipped via liveStart.
    uniformSortedIdx = Int32Array.from({ length: activePixels.length }, (_, i) => i);
    uniformSortedIdx.sort((a, b) => uniformExtras[a * UNIFORM_FIELDS] - uniformExtras[b * UNIFORM_FIELDS]);
  }

  // Thanos mode: pre-compute per-cluster random 360° direction + speed + death time.
  // Fields: [dirX, dirY, speed, deathTime, noise]
  const THANOS_FIELDS = 5;
  let thanosExtras = null;
  if (mode === "thanos") {
    thanosExtras = new Float32Array(activePixels.length * THANOS_FIELDS);
    for (let i = 0; i < activePixels.length; i++) {
      const p = activePixels[i];
      const angle = hashNoise(p.x * 0.11, p.y * 0.09, seed) * Math.PI * 2;
      const speed = 0.04 + hashNoise(p.x * 0.07, p.y * 0.13, seed + 1) * 0.10;
      const deathTime = hashNoise(p.x * 0.05, p.y * 0.05, seed + 2) * 0.35; // stagger start [0..0.35]
      const noise = hashNoise(p.x * 0.42, p.y * 0.37, seed + 3);
      const off = i * THANOS_FIELDS;
      thanosExtras[off]     = Math.cos(angle); // dirX
      thanosExtras[off + 1] = Math.sin(angle); // dirY
      thanosExtras[off + 2] = speed;
      thanosExtras[off + 3] = deathTime;
      thanosExtras[off + 4] = noise;
    }
  }

  const relativeLeft = rect.left - hostRect.left - burstPadding / scale;
  const relativeTop = rect.top - hostRect.top - burstPadding / scale;
  const hostStyle = window.getComputedStyle(node);
  const restorePosition = hostStyle.position === "static" ? node.style.position : null;
  const restoreOverflow = node.style.overflow;
  const expandedCssWidth = expandedWidth / scale;
  const expandedCssHeight = expandedHeight / scale;

  if (hostStyle.position === "static") {
    node.style.position = "relative";
  }
  node.style.overflow = "visible";

  overlayCanvas.width = expandedWidth;
  overlayCanvas.height = expandedHeight;
  overlayCanvas.style.position = "absolute";
  overlayCanvas.style.left = `${relativeLeft}px`;
  overlayCanvas.style.top = `${relativeTop}px`;
  overlayCanvas.style.width = `${expandedCssWidth}px`;
  overlayCanvas.style.height = `${expandedCssHeight}px`;
  overlayCanvas.style.pointerEvents = "none";
  overlayCanvas.style.zIndex = "9999";
  overlayCanvas.style.overflow = "visible";

  frameCanvas.width = expandedWidth;
  frameCanvas.height = expandedHeight;

  overlayContext.imageSmoothingEnabled = true;
  frameContext.putImageData(imageData, burstPadding, burstPadding);

  node.appendChild(overlayCanvas);
  overlayCanvas.style.opacity = "1";
  overlayCanvas.style.visibility = "visible";
  overlayContext.clearRect(0, 0, expandedWidth, expandedHeight);
  overlayContext.drawImage(frameCanvas, 0, 0);
  targetNode.style.visibility = "hidden";

  // Thanos: collapse layout space so remaining cards shift up almost immediately.
  // negative margin-bottom pulls siblings up without clipping the abs-positioned canvas.
  let collapseTimerId = 0;
  const restoreHeight = node.style.height;
  const restoreMarginTop = node.style.marginTop;
  const restoreTransition = node.style.transition;
  if (mode === "thanos") {
    // Animate height → 0 + margin-top → 0. With overflow:visible the canvas overlay
    // stays visible. Layout contribution reaches 0 before wrapper is removed → no jump.
    const wrapperHeight = node.getBoundingClientRect().height;
    const currentMarginTop = parseFloat(window.getComputedStyle(node).marginTop) || 0;
    node.style.height = `${wrapperHeight}px`;
    node.style.boxSizing = "border-box";
    collapseTimerId = window.setTimeout(() => {
      node.style.transition = "height 350ms ease-in-out, margin-top 350ms ease-in-out";
      node.style.height = "0px";
      if (currentMarginTop > 0) node.style.marginTop = "0px";
    }, introHoldDuration);
  }

  let animationFrameId = 0;
  let finished = false;
  let nodeHidden = true;

  return new Promise((resolve) => {
    const finish = (aborted = false) => {
      if (finished) return;
      finished = true;
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(collapseTimerId);
      overlayCanvas.remove();
      if (restorePosition !== null) {
        node.style.position = restorePosition;
      }
      node.style.overflow = restoreOverflow;
      // For thanos (non-aborted): don't restore margin — the wrapper is about to be
      // removed by React. Restoring margin before removal causes a one-frame jump.
      if (mode !== "thanos" || aborted) {
        node.style.height = restoreHeight;
        node.style.marginTop = restoreMarginTop;
        node.style.transition = restoreTransition;
      }
      if (aborted && nodeHidden) {
        targetNode.style.visibility = "";
      }
      resolve();
    };

    if (signal) {
      signal.addEventListener("abort", () => finish(true), { once: true });
    }

    const startTime = performance.now();

    const renderFrame = (timestamp) => {
      if (signal?.aborted) {
        finish(true);
        return;
      }
      const elapsed = timestamp - startTime;
      const handoffElapsed = Math.max(0, elapsed - introHoldDuration);
      const effectiveElapsed = Math.max(0, handoffElapsed - handoffDuration);
      const dissolveProgress = clamp(effectiveElapsed / duration, 0, 1);
      const easedProgress = easeOutCubic(dissolveProgress);
      const lingerProgress =
        effectiveElapsed <= duration ? 0 : clamp((effectiveElapsed - duration) / lingerDuration, 0, 1);
      const lingerAlpha = lingerProgress > 0 ? 1 - lingerProgress ** 1.35 : 1;

      if (elapsed < introHoldDuration) {
        overlayContext.clearRect(0, 0, expandedWidth, expandedHeight);
        overlayContext.drawImage(frameCanvas, 0, 0);
        animationFrameId = window.requestAnimationFrame(renderFrame);
        return;
      }

      if (handoffElapsed < handoffDuration) {
        animationFrameId = window.requestAnimationFrame(renderFrame);
        return;
      }

      const edgeWidth = 12;
      const fullyGoneOffset = 10;
      const particleDensity = 0.42;
      framePixels.fill(0);

      if (mode !== "uniform") {
        // ── SWEEP MODE (original left-to-right front) ──────────────────────
        for (let y = 0; y < height; y += 1) {
          frontByRow[y] = buildHorizontalFront(width, easedProgress, y, seed);
          streamByRow[y] = Math.sin(y * 0.082 + easedProgress * 10.2 + seed * 0.3);
        }

        for (let index = 0; index < activePixels.length; index += 1) {
          const particle = activePixels[index];
          const sampleX = particle.x;
          const sampleY = particle.y;
          const baseX = burstPadding + sampleX;
          const baseY = burstPadding + sampleY;
          const noise = hashNoise(sampleX * 0.42 + easedProgress * 8.3, sampleY * 0.37, seed);
          let alphaScale = 1;
          let scatterAmount = 0;

          const distance = sampleX - frontByRow[sampleY];
          if (distance <= -fullyGoneOffset) {
            continue;
          } else if (distance < edgeWidth) {
            const normalized = clamp((distance + fullyGoneOffset) / (edgeWidth + fullyGoneOffset), 0, 1);
            const grain = noise > normalized * particleDensity ? 0 : 1;
            alphaScale = grain ? 0.72 + normalized * 0.28 : 0;
            scatterAmount = (1 - normalized) * clamp(easedProgress * 1.2, 0, 1);
          }

          if (alphaScale <= 0) continue;

          const unit = resolveUpwardScatter(sampleX, sampleY, easedProgress);
          const lateralJitter = scatterAmount > 0
            ? (hashNoise(sampleX * 0.61, sampleY * 0.29, seed) - 0.5) * 8 * scatterAmount : 0;
          const evaporateLift = scatterAmount > 0 || lingerProgress > 0
            ? (7 + noise * 12) * scatterAmount * (0.34 + easedProgress * 0.66) + (8 + noise * 12) * lingerProgress : 0;
          const scatterDistance = scatterAmount > 0
            ? (9 + noise * 16) * (0.62 + scatterAmount * 1.02) * (0.38 + easedProgress * 0.56) : 0;
          const wind = resolveWindDrift(sampleX, sampleY, easedProgress, seed);
          const windStrength = scatterAmount > 0
            ? (4.8 + noise * 8.6) * scatterAmount * (0.42 + easedProgress * 0.98) : 0;
          const streamOffset = scatterAmount > 0
            ? streamByRow[sampleY] * (1.2 + noise * 3.2) * scatterAmount : 0;
          const frontPull = scatterAmount > 0
            ? (1.4 + noise * 2.8) * scatterAmount * (0.34 + easedProgress * 0.3) : 0;

          const rawTargetX = Math.round(baseX + frontPull + unit.x * scatterDistance + lateralJitter + wind.x * windStrength + streamOffset);
          const rawTargetY = Math.round(baseY + unit.y * scatterDistance - evaporateLift + wind.y * windStrength * 0.62);
          const targetX = clamp(rawTargetX, 0, expandedWidth - 1);
          const targetY = clamp(rawTargetY, 0, expandedHeight - 1);
          const drawIndex = (targetY * expandedWidth + targetX) * 4;
          const tinted = resolveParticleTint(particle, scatterAmount, noise, lingerProgress);
          const nextAlpha = Math.round(clamp(particle.a * alphaScale * lingerAlpha * tinted.alphaBoost, 0, 255));
          if (nextAlpha <= framePixels[drawIndex + 3]) continue;
          framePixels[drawIndex] = tinted.r;
          framePixels[drawIndex + 1] = tinted.g;
          framePixels[drawIndex + 2] = tinted.b;
          framePixels[drawIndex + 3] = nextAlpha;
        }
      } else {
        // ── UNIFORM MODE (all pixels die randomly, pre-computed data) ──────
        // Advance liveStart past fully-dead particles (sorted by deathTime ascending).
        while (liveStart < uniformSortedIdx.length) {
          const si = uniformSortedIdx[liveStart];
          if (uniformExtras[si * UNIFORM_FIELDS] + UNIFORM_EDGE_ZONE > easedProgress) break;
          liveStart++;
        }

        for (let k = liveStart; k < uniformSortedIdx.length; k++) {
          const particleIdx = uniformSortedIdx[k];
          const particle = activePixels[particleIdx];
          const sampleX = particle.x;
          const sampleY = particle.y;
          const off = particleIdx * UNIFORM_FIELDS;
          const deathTime = uniformExtras[off];
          const noise     = uniformExtras[off + 1]; // static, pre-computed
          const latBase   = uniformExtras[off + 2];
          const unitX     = uniformExtras[off + 3];
          const unitY     = uniformExtras[off + 4];

          // Pixels with deathTime > easedProgress are fully alive — no scatter needed.
          if (easedProgress <= deathTime) {
            // Draw pixel in-place, no movement.
            const drawIdx = ((burstPadding + sampleY) * expandedWidth + (burstPadding + sampleX)) * 4;
            if (particle.a > framePixels[drawIdx + 3]) {
              framePixels[drawIdx]     = particle.r;
              framePixels[drawIdx + 1] = particle.g;
              framePixels[drawIdx + 2] = particle.b;
              framePixels[drawIdx + 3] = particle.a;
            }
            continue;
          }

          const normalized = (easedProgress - deathTime) / UNIFORM_EDGE_ZONE;
          const alphaScale = 1 - normalized;
          const scatterAmount = normalized;

          const evaporateLift = (7 + noise * 12) * scatterAmount * (0.34 + easedProgress * 0.66) + (8 + noise * 12) * lingerProgress;
          const scatterDistance = (9 + noise * 16) * (0.62 + scatterAmount * 1.02) * (0.38 + easedProgress * 0.56);
          const wind = resolveWindDrift(sampleX, sampleY, easedProgress, seed);
          const windStrength = (4.8 + noise * 8.6) * scatterAmount * (0.42 + easedProgress * 0.98);
          const streamVal = Math.sin(sampleY * 0.082 + easedProgress * 10.2 + seed * 0.3);

          const rawTargetX = Math.round(burstPadding + sampleX + unitX * scatterDistance + latBase * scatterAmount + wind.x * windStrength + streamVal * (1.2 + noise * 3.2) * scatterAmount);
          const rawTargetY = Math.round(burstPadding + sampleY + unitY * scatterDistance - evaporateLift + wind.y * windStrength * 0.62);
          const targetX = clamp(rawTargetX, 0, expandedWidth - 1);
          const targetY = clamp(rawTargetY, 0, expandedHeight - 1);
          const drawIndex = (targetY * expandedWidth + targetX) * 4;
          const tinted = resolveParticleTint(particle, scatterAmount, noise, lingerProgress);
          const nextAlpha = Math.round(clamp(particle.a * alphaScale * lingerAlpha * tinted.alphaBoost, 0, 255));
          if (nextAlpha <= framePixels[drawIndex + 3]) continue;
          framePixels[drawIndex]     = tinted.r;
          framePixels[drawIndex + 1] = tinted.g;
          framePixels[drawIndex + 2] = tinted.b;
          framePixels[drawIndex + 3] = nextAlpha;
        }
      }

      if (mode === "thanos") {
        // ── THANOS MODE — ImageData write (one putImageData per frame, no per-particle state changes) ──
        framePixels.fill(0);
        const maxTravel = Math.max(width, height) * 1.1;
        const cs = 3; // cluster size

        for (let i = 0; i < activePixels.length; i++) {
          const p = activePixels[i];
          const off = i * THANOS_FIELDS;
          const dirX      = thanosExtras[off];
          const dirY      = thanosExtras[off + 1];
          const speed     = thanosExtras[off + 2];
          const deathTime = thanosExtras[off + 3];
          const noise     = thanosExtras[off + 4];

          let tx, ty, finalAlpha;
          if (easedProgress < deathTime) {
            tx = burstPadding + p.x;
            ty = burstPadding + p.y;
            finalAlpha = Math.round(p.a * lingerAlpha);
          } else {
            const localProgress = Math.min((easedProgress - deathTime) / (1 - deathTime + 0.001), 1);
            const travel = localProgress * speed * maxTravel * (0.5 + noise * 0.6);
            const alpha = Math.max(0, 1 - localProgress ** 0.7) * lingerAlpha;
            if (alpha < 0.01) continue;
            tx = Math.round(burstPadding + p.x + dirX * travel);
            ty = Math.round(burstPadding + p.y + dirY * travel);
            finalAlpha = Math.round(p.a * alpha);
          }

          // Clamp Y to card bottom so particles don't overlap cards below.
          ty = Math.min(ty, burstPadding + height - 1);

          for (let dy = 0; dy < cs; dy++) {
            const py = clamp(ty - 1 + dy, 0, burstPadding + height - 1);
            for (let dx = 0; dx < cs; dx++) {
              const px = clamp(tx - 1 + dx, 0, expandedWidth - 1);
              const idx = (py * expandedWidth + px) * 4;
              if (finalAlpha > framePixels[idx + 3]) {
                framePixels[idx]     = p.r;
                framePixels[idx + 1] = p.g;
                framePixels[idx + 2] = p.b;
                framePixels[idx + 3] = finalAlpha;
              }
            }
          }
        }
        frameContext.putImageData(frameImage, 0, 0);
        overlayContext.clearRect(0, 0, expandedWidth, expandedHeight);
        overlayContext.drawImage(frameCanvas, 0, 0);
      } else {
        frameContext.putImageData(frameImage, 0, 0);
        overlayContext.clearRect(0, 0, expandedWidth, expandedHeight);
        overlayContext.drawImage(frameCanvas, 0, 0);
      }

      if (
        elapsed >= introHoldDuration + handoffDuration &&
        dissolveProgress >= 1 &&
        lingerProgress >= 1
      ) {
        finish(false);
        return;
      }

      animationFrameId = window.requestAnimationFrame(renderFrame);
    };

    animationFrameId = window.requestAnimationFrame(renderFrame);
  });
}

export function TelegramDissolveRemoval({
  children,
  className = "",
  duration = 1280,
  introHoldDuration = 220,
  handoffDuration = 0,
  lingerDuration = 520,
  startDelay = 0,
  mode = "sweep",
  onComplete,
  onEngineChange,
}) {
  const hostRef = useRef(null);
  const [removed, setRemoved] = useState(false);
  const [fallbackActive, setFallbackActive] = useState(false);

  // Use refs for callbacks so changing them doesn't restart the dissolve.
  // Inline arrow functions passed as props get new references on every parent
  // render, which would cause the useEffect to re-run and replay the animation.
  const onCompleteRef = useRef(onComplete);
  const onEngineChangeRef = useRef(onEngineChange);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => { onEngineChangeRef.current = onEngineChange; }, [onEngineChange]);

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let fallbackTimeoutId = 0;
    let startTimeoutId = 0;

    const run = async () => {
      try {
        onEngineChangeRef.current?.("bitmap");
        await playTelegramDissolve(hostRef.current, {
          duration,
          introHoldDuration,
          handoffDuration,
          lingerDuration,
          mode,
          signal: controller.signal,
        });

        if (!disposed && !controller.signal.aborted) {
          onCompleteRef.current?.();
          setRemoved(true);
        }
      } catch (error) {
        console.warn("Telegram dissolve animation failed, falling back to CSS removal.", error);
        if (!disposed && !controller.signal.aborted) {
          onEngineChangeRef.current?.("fallback", error);
          setFallbackActive(true);
          fallbackTimeoutId = window.setTimeout(() => {
            onCompleteRef.current?.();
            setRemoved(true);
          }, introHoldDuration + handoffDuration + duration + lingerDuration);
        }
      }
    };

    if (startDelay > 0) {
      startTimeoutId = window.setTimeout(run, startDelay);
    } else {
      run();
    }

    return () => {
      disposed = true;
      if (startTimeoutId) {
        window.clearTimeout(startTimeoutId);
      }
      if (fallbackTimeoutId) {
        window.clearTimeout(fallbackTimeoutId);
      }
      controller.abort();
    };
  // onComplete/onEngineChange intentionally excluded — handled via refs above
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, introHoldDuration, handoffDuration, lingerDuration, startDelay, mode]);

  if (removed) return null;

  return (
    <div
      ref={hostRef}
      className={`${className}${fallbackActive ? " telegram-dissolve-fallback" : ""}`}
    >
      {children}
    </div>
  );
}
